import React, { useState, useRef, useEffect } from 'react';
import { EditableText } from '@blueprintjs/core';
import './InlineEditableText.css';

interface InlineEditableTextProps {
  value: string;
  onSave: (newValue: string) => Promise<void> | void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  multiline?: boolean;
  selectAllOnFocus?: boolean;
  minWidth?: number;
}

const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  onSave,
  placeholder = 'Click to edit',
  className = '',
  maxLength = 100,
  multiline = false,
  selectAllOnFocus = true,
  minWidth = 50,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleConfirm = async (newValue: string) => {
    if (newValue.trim() === value.trim()) {
      setIsEditing(false);
      return;
    }

    if (newValue.trim().length === 0) {
      setEditValue(value);
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(newValue.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      setEditValue(value); // Reset on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  return (
    <div 
      className={`inline-editable-text ${className} ${isEditing ? 'editing' : ''} ${isSaving ? 'saving' : ''}`}
      style={{ minWidth }}
    >
      <EditableText
        value={editValue}
        onChange={setEditValue}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onEdit={() => setIsEditing(true)}
        placeholder={placeholder}
        maxLength={maxLength}
        multiline={multiline}
        selectAllOnFocus={selectAllOnFocus}
        disabled={isSaving}
      />
    </div>
  );
};

export default InlineEditableText;
